// src/components/matchmaking/Matchmaking.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · AI Matchmaking
//  "TelcoFair Glass Workspace" — same system as Companies.jsx / Dashboard.jsx
//  Layout is pure CSS Grid with the same design tokens and primitives
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Skeleton,
    Tabs,
    Tab,
    CircularProgress,
    Fade,
    Divider,
    Alert,
    Card,
    CardContent,
    Stack,
    Grid,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Refresh as RefreshIcon,
    Business as BusinessIcon,
    VerifiedUser as VerifiedUserIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    LocationOn as LocationIcon,
    Language as LanguageIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    Verified as VerifiedIcon,
    FiberManualRecord as LiveIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Storefront as StorefrontIcon,
    Close as CloseIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Check as CheckIcon,
    Download as DownloadIcon,
    Subscriptions as SubscriptionsIcon,
    WorkspacePremium as PackageIcon,
    EmojiEvents as EmojiEventsIcon,
    FilterAltOff as FilterOffIcon,
    Groups as GroupsIcon,
    CalendarToday as CalendarIcon,
    NotesOutlined as NotesIcon,
    People as PeopleIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    Stars as StarsIcon,
    AutoAwesome as AutoAwesomeIcon,
    Psychology as PsychologyIcon,
    Handshake as HandshakeIcon,
    Recommend as RecommendIcon,
    ArrowForward as CompareArrowsIcon,
    Insights as InsightsIcon,
    Lightbulb as LightbulbIcon,
    Send as SendIcon,
    ArrowForward as ArrowForwardIcon,
    Public as PublicIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
    Star as StarIcon,
    DonutLarge as DonutLargeIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    setDoc,
    serverTimestamp,
    addDoc,
    query,
    where,
    orderBy,
    limit,
} from 'firebase/firestore';
import toast from 'react-hot-toast';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS (identical to Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const COMPANY_TYPES = [
    { value: 'operator', label: 'Operator', accent: palette.rust },
    { value: 'vendor', label: 'Vendor', accent: palette.plum },
    { value: 'aggregator', label: 'Aggregator', accent: palette.teal },
    { value: 'solution_provider', label: 'Solution Provider', accent: palette.pine },
    { value: 'consultant', label: 'Consultant', accent: palette.amber },
    { value: 'mvno', label: 'MVNO', accent: palette.indigo },
    { value: 'reseller', label: 'Reseller', accent: palette.tealGlow },
];

const typeMeta = (value) =>
    COMPANY_TYPES.find((t) => t.value === value) || {
        value: value || 'other',
        label: value ? String(value).replace(/_/g, ' ') : 'Unspecified',
        accent: palette.inkLight,
    };

const REGIONS = ['North America', 'Latin America', 'Europe', 'Asia Pacific', 'Middle East', 'Africa'];

const SERVICE_CATEGORIES = [
    'Voice', 'SMS', 'A2P', 'CPaaS', 'eSIM', 'Roaming',
    'Anti-Fraud', 'Data', 'Cloud', 'Vendor Solutions',
    'Wholesale Voice', 'Retail', 'MVNO', 'Interconnect',
    'IoT', '5G', 'SD-WAN', 'API Management', 'Security Solutions',
    'Consulting', 'Training', 'Platform Services', 'Infrastructure',
];

/* ═══════════════════════════════════════════════════════════════════════════
   3 · AI MATCHING ENGINE (FIXED - SAFE STRING HANDLING)
   ═══════════════════════════════════════════════════════════════════════════ */

// Helper to safely convert any value to lowercase string
const safeString = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value.toLowerCase().trim();
    if (typeof value === 'number') return String(value).toLowerCase().trim();
    if (typeof value === 'boolean') return String(value).toLowerCase().trim();
    if (value && typeof value === 'object') {
        // Handle Firestore Timestamp or other objects
        try {
            return String(value).toLowerCase().trim();
        } catch {
            return '';
        }
    }
    return String(value).toLowerCase().trim();
};

const calculateAIMatchScore = (sourceCompany, targetCompany) => {
    // If either company is missing, return minimal score
    if (!sourceCompany || !targetCompany) {
        return { score: 0, details: [], mutualBenefit: false, providerToSeeker: [], seekerToProvider: [] };
    }

    let score = 0;
    const details = [];
    const providerToSeeker = [];
    const seekerToProvider = [];

    // Helper to get service arrays safely - now using safeString
    const getServices = (company) => {
        const services = [];
        if (company.primaryService) services.push(safeString(company.primaryService));
        if (company.secondaryService) services.push(safeString(company.secondaryService));
        if (Array.isArray(company.services)) {
            company.services.forEach(s => {
                if (s) services.push(safeString(s));
            });
        }
        return services.filter(Boolean);
    };

    // Helper to get needs arrays safely - now using safeString
    const getNeeds = (company) => {
        const needs = [];
        if (company.primaryConnectWith) needs.push(safeString(company.primaryConnectWith));
        if (company.secondaryConnectWith) needs.push(safeString(company.secondaryConnectWith));
        if (company.otherConnectWith) needs.push(safeString(company.otherConnectWith));
        if (Array.isArray(company.connectWithNeeds)) {
            company.connectWithNeeds.forEach(n => {
                if (n) needs.push(safeString(n));
            });
        }
        return needs.filter(Boolean);
    };

    const sourceServices = getServices(sourceCompany);
    const sourceNeeds = getNeeds(sourceCompany);
    const targetServices = getServices(targetCompany);
    const targetNeeds = getNeeds(targetCompany);

    const sourceName = sourceCompany.companyName || 'Company A';
    const targetName = targetCompany.companyName || 'Company B';

    // 1. Provider → Seeker matches (source provides what target needs)
    sourceServices.forEach(service => {
        targetNeeds.forEach(need => {
            if (service && need) {
                const isMatch = service === need ||
                    service.includes(need) ||
                    need.includes(service) ||
                    service.split(' ').some(w => need.includes(w)) ||
                    need.split(' ').some(w => service.includes(w));
                if (isMatch) {
                    providerToSeeker.push({
                        providerService: service,
                        seekerNeed: need,
                        providerName: sourceName,
                        seekerName: targetName
                    });
                    details.push(`${sourceName} PROVIDES "${service}" which ${targetName} NEEDS ✓`);
                }
            }
        });
    });

    // 2. Seeker → Provider matches (target provides what source needs)
    targetServices.forEach(service => {
        sourceNeeds.forEach(need => {
            if (service && need) {
                const isMatch = service === need ||
                    service.includes(need) ||
                    need.includes(service) ||
                    service.split(' ').some(w => need.includes(w)) ||
                    need.split(' ').some(w => service.includes(w));
                if (isMatch) {
                    seekerToProvider.push({
                        providerService: service,
                        seekerNeed: need,
                        providerName: targetName,
                        seekerName: sourceName
                    });
                    details.push(`${targetName} PROVIDES "${service}" which ${sourceName} NEEDS ✓`);
                }
            }
        });
    });

    // Calculate scores
    const providerToSeekerScore = Math.min(40, providerToSeeker.length * 20);
    const seekerToProviderScore = Math.min(40, seekerToProvider.length * 20);
    score += providerToSeekerScore + seekerToProviderScore;

    // 3. Mutual benefit bonus
    let mutualBenefit = false;
    if (providerToSeeker.length > 0 && seekerToProvider.length > 0) {
        mutualBenefit = true;
        score += 20;
        details.push(`🎯 MUTUAL BENEFIT: Both companies have services the other needs!`);
    }

    // 4. Region match (10 pts) - using safeString
    const sourceRegions = [sourceCompany.primaryRegion, sourceCompany.secondaryRegion]
        .filter(Boolean).map(r => safeString(r));
    const targetRegions = [targetCompany.primaryRegion, targetCompany.secondaryRegion]
        .filter(Boolean).map(r => safeString(r));

    let regionScore = 0;
    sourceRegions.forEach(sr => {
        targetRegions.forEach(tr => {
            if (sr === tr) {
                regionScore += 5;
                details.push(`Both companies operate in ${sr}`);
            }
        });
    });
    regionScore = Math.min(10, regionScore);
    score += regionScore;

    // 5. Company type synergy (5 pts) - using safeString
    const sourceType = safeString(sourceCompany.companyType) || 'other';
    const targetType = safeString(targetCompany.companyType) || 'other';
    if (sourceType === targetType && sourceType !== 'other') {
        score += 5;
        details.push(`Both companies are ${sourceType}s`);
    }

    // 6. Verification bonus (5 pts)
    if (sourceCompany.isVerified && targetCompany.isVerified) {
        score += 5;
        details.push('Both companies are verified');
    }

    // Cap at 100
    score = Math.min(Math.round(score), 100);

    // Generate AI insights
    let aiInsights = {
        summary: '',
        recommendation: '',
        subject: '',
    };

    if (mutualBenefit) {
        aiInsights.summary = '🌟 PERFECT MATCH! Both companies can benefit from each other\'s services.';
        aiInsights.subject = '🎯 Mutual Benefit Partnership Opportunity';
        aiInsights.recommendation =
            `This is a mutual benefit match! ${sourceName} provides ${providerToSeeker.map(m => m.providerService).join(', ')} that ${targetName} needs, and ${targetName} provides ${seekerToProvider.map(m => m.providerService).join(', ')} that ${sourceName} needs. This creates a strong foundation for a win-win partnership.`;
    } else if (providerToSeeker.length > 0) {
        aiInsights.summary = `📊 Strong Match: ${sourceName} can provide services that ${targetName} needs.`;
        aiInsights.subject = `🤝 Service Provider Match (${score}%)`;
        aiInsights.recommendation =
            `${sourceName} is a service provider for ${targetName}. ${targetName} should consider reaching out to ${sourceName} for their service needs.`;
    } else if (seekerToProvider.length > 0) {
        aiInsights.summary = `📊 Strong Match: ${targetName} can provide services that ${sourceName} needs.`;
        aiInsights.subject = `🤝 Service Provider Match (${score}%)`;
        aiInsights.recommendation =
            `${targetName} is a service provider for ${sourceName}. ${sourceName} should consider reaching out to ${targetName} for their service needs.`;
    } else {
        aiInsights.summary = `🔍 Exploratory Match: Exploring potential synergies between companies.`;
        aiInsights.subject = `📈 Exploratory Partnership (${score}%)`;
        aiInsights.recommendation =
            `While there may not be direct service matches, both companies operate in the telecom space. Consider exploring broader partnership opportunities.`;
    }

    return {
        score,
        details,
        mutualBenefit,
        providerToSeeker,
        seekerToProvider,
        aiInsights,
    };
};

const generateAIMessage = (sourceCompany, targetCompany, score, details, mutualBenefit) => {
    const sourceName = sourceCompany?.companyName || 'Your Company';
    const targetName = targetCompany?.companyName || 'the company';

    let matchLevel = 'moderate';
    let emoji = '📊';
    if (score >= 80) { matchLevel = 'exceptional';
        emoji = '🎯'; } else if (score >= 60) { matchLevel = 'strong';
        emoji = '🤝'; } else if (score >= 40) { matchLevel = 'good';
        emoji = '📈'; } else { matchLevel = 'exploratory';
        emoji = '🔍'; }

    let mutualBenefitSection = '';
    if (mutualBenefit) {
        mutualBenefitSection = `
**MUTUAL BENEFIT PARTNERSHIP 🎯**

This is a perfect match! Both companies have what the other needs.
This creates a win-win situation where both companies can benefit from each other's expertise.`;
    }

    let matchDetailsSection = '';
    if (details && details.length > 0) {
        matchDetailsSection = `\n\n**Why This Partnership Makes Sense:**\n${details.slice(0, 6).map(d => `- ${d}`).join('\n')}`;
    }

    return {
        subject: `${emoji} ${matchLevel.charAt(0).toUpperCase() + matchLevel.slice(1)} Partnership Opportunity: ${sourceName} - ${targetName} (${score}% Match)`,
        message: `Dear ${targetName} Team,

I hope this message finds you well. I am reaching out on behalf of ${sourceName} to explore a potential partnership opportunity.

Our AI-powered matching system has identified a **${matchLevel}** synergy (${score}% match) between our organizations.${mutualBenefitSection}${matchDetailsSection}

**Why This Partnership Matters:**

- Combined expertise creates new market opportunities
- Complementary strengths allow for innovative solutions
- Shared values and vision for the telecom industry

We would be delighted to schedule a meeting to discuss how we can collaborate and create mutual value.

Looking forward to connecting!

Warm regards,
The ${sourceName} Team`
    };
};

/* ═══════════════════════════════════════════════════════════════════════════
   4 · LIQUID PRIMITIVES (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const TypePill = ({ type }) => {
    const meta = typeMeta(type);
    return <MetaPill label={meta.label} tone={meta.accent} />;
};

const CompanyAvatar = ({ company, size = 40, radius = 14 }) => {
    const accent = company?.isBlocked ? palette.inkLight : typeMeta(company?.companyType).accent;
    return (
        <Avatar
            src={company?.logo || company?.profileImage || ''}
            sx={{
                width: size, height: size, borderRadius: `${radius}px`, flexShrink: 0,
                fontFamily: fontMono, fontSize: size * 0.34, fontWeight: 700, color: '#fff',
                background: `linear-gradient(150deg, ${accent}, ${rgba(accent, 0.68)})`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 8px 18px -8px ${rgba(accent, 0.85)}`,
                filter: company?.isBlocked ? 'grayscale(0.6)' : 'none',
            }}
        >
            {(company?.companyName || 'C').charAt(0).toUpperCase()}
        </Avatar>
    );
};

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   5 · COMPATIBILITY SCORE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const CompatibilityScore = ({ score }) => {
    const getColor = (value) => {
        if (value >= 80) return palette.pine;
        if (value >= 60) return palette.amber;
        if (value >= 40) return palette.rust;
        return palette.inkLight;
    };

    const getLabel = (value) => {
        if (value >= 80) return 'Excellent';
        if (value >= 60) return 'Good';
        if (value >= 40) return 'Potential';
        return 'Low';
    };

    const color = getColor(score);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    variant="determinate"
                    value={Math.min(score, 100)}
                    size={30}
                    thickness={4}
                    sx={{
                        color: color,
                        '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                    }}
                />
                <Box
                    sx={{
                        top: 0, left: 0, bottom: 0, right: 0,
                        position: 'absolute',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <Typography sx={{ fontFamily: fontMono, fontWeight: 700, fontSize: '0.55rem', color: palette.ink }}>
                        {Math.round(score)}%
                    </Typography>
                </Box>
            </Box>
            <Typography sx={{ fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600, color }}>
                {getLabel(score)}
            </Typography>
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 5) % 8),
                x: (i * 41) % 100,
                y: (i * 59) % 100,
                duration: 28 + ((i * 4) % 16),
                delay: (i * 1.9) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-matchmaking-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.009 0.013" numOctaves="2" seed="11" result="noise">
                            <animate attributeName="baseFrequency" dur="32s" values="0.009 0.013;0.015 0.007;0.009 0.013" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="66" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -60, 40, 0], y: [0, 40, -30, 0], scale: [1, 1.12, 0.92, 1] }}
                transition={{ duration: 31, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-8%', left: '-5%',
                    width: 600, height: 600, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.3)}, ${rgba(palette.rust, 0.12)} 46%, transparent 72%)`,
                    filter: 'url(#tf-matchmaking-liquid) blur(44px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 60, -35, 0], y: [0, -40, 30, 0], scale: [1, 0.9, 1.14, 1] }}
                transition={{ duration: 37, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-12%', right: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.22)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-matchmaking-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-56%', '-45%', '-50%'], y: ['-50%', '-45%', '-56%', '-50%'], scale: [1, 1.07, 0.95, 1] }}
                transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '42%', left: '52%',
                    width: 720, height: 720, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.15)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 40}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 12}%`, `${m.x}%`],
                        opacity: [0, 0.45, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.5,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   7 · TAB PANEL
   ═══════════════════════════════════════════════════════════════════════════ */

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ py: 3, width: '100%' }}>{children}</Box>}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   8 · RECOMMENDATION CARD
   ═══════════════════════════════════════════════════════════════════════════ */

const RecommendationCard = ({
    company,
    score,
    matchDetails,
    mutualBenefit,
    providerToSeeker,
    seekerToProvider,
    onConnect,
    onViewProfile,
    onAIAnalysis,
    index,
}) => {
    const [expanded, setExpanded] = useState(false);

    const getScoreColor = () => {
        if (score >= 80) return palette.pine;
        if (score >= 60) return palette.amber;
        if (score >= 40) return palette.rust;
        return palette.inkLight;
    };

    const color = getScoreColor();
    const isProvider = providerToSeeker && providerToSeeker.length > 0;
    const isSeeker = seekerToProvider && seekerToProvider.length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: '20px',
                    border: `1.5px solid ${rgba(color, 0.15)}`,
                    background: `linear-gradient(135deg, ${palette.paper} 0%, ${rgba(color, 0.04)} 100%)`,
                    boxShadow: `0 4px 24px ${rgba(color, 0.08)}, 0 8px 40px ${rgba('#000', 0.04)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${rgba(color, 0.15)}, 0 8px 40px ${rgba('#000', 0.08)}`,
                        borderColor: `${color}50`,
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${color}, ${color}80, ${color})`,
                        opacity: 0.6,
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -80,
                        right: -80,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${rgba(color, 0.08)}, transparent 70%)`,
                        filter: 'blur(40px)',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                        <CompanyAvatar company={company} size={52} radius={14} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: palette.ink }}>
                                    {company?.companyName || 'Unnamed Company'}
                                </Typography>
                                {company?.isVerified && <VerifiedIcon sx={{ fontSize: 16, color: palette.pine }} />}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                <TypePill type={company?.companyType} />
                                {company?.headquarters && (
                                    <MetaPill
                                        label={company.headquarters}
                                        tone={palette.teal}
                                        icon={<LocationIcon sx={{ fontSize: 12 }} />}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompatibilityScore score={score} />
                        {mutualBenefit && (
                            <MetaPill label="Mutual" tone={palette.pine} icon={<HandshakeIcon sx={{ fontSize: 12 }} />} />
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                    {isProvider && (
                        <MetaPill
                            label={`Provides: ${providerToSeeker.map(m => m.providerService).join(', ')}`}
                            tone={palette.pine}
                            icon={<HandshakeIcon sx={{ fontSize: 12 }} />}
                        />
                    )}
                    {isSeeker && (
                        <MetaPill
                            label={`Needs: ${seekerToProvider.map(m => m.seekerNeed).join(', ')}`}
                            tone={palette.plum}
                            icon={<StarsIcon sx={{ fontSize: 12 }} />}
                        />
                    )}
                    {company?.primaryService && !isProvider && (
                        <MetaPill label={company.primaryService} tone={palette.amber} />
                    )}
                    {(company?.regions || []).slice(0, 2).map((r) => (
                        <MetaPill key={r} label={r} tone={palette.teal} icon={<PublicIcon sx={{ fontSize: 12 }} />} />
                    ))}
                </Box>

                {expanded && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${palette.hairlineSoft}` }}>
                        {company?.description && (
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.82rem', lineHeight: 1.6, mb: 1.5 }}>
                                {company.description}
                            </Typography>
                        )}

                        {mutualBenefit && (
                            <Alert severity="success" sx={{ mb: 1.5, borderRadius: '12px', py: 0.75, bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.15)}` }}>
                                <Typography sx={{ fontSize: '0.78rem', color: palette.pine, fontWeight: 600 }}>
                                    🎯 Mutual Benefit: Both companies have what the other needs!
                                </Typography>
                            </Alert>
                        )}

                        {matchDetails && matchDetails.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint, mb: 0.5 }}>
                                    Match Details
                                </Typography>
                                {matchDetails.slice(0, 5).map((detail, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                        <CheckIcon sx={{ fontSize: 13, color: palette.pine, mt: 0.25 }} />
                                        <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft }}>
                                            {detail}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: `1px solid ${palette.hairlineSoft}` }}>
                    <Button
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', color: palette.inkLight }}
                    >
                        {expanded ? 'Show less' : 'Show more'}
                    </Button>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="AI Analysis">
                            <IconButton
                                size="small"
                                onClick={() => onAIAnalysis(company)}
                                sx={{
                                    width: 32, height: 32, borderRadius: '10px',
                                    color: palette.plum,
                                    border: `1px solid ${rgba(palette.plum, 0.2)}`,
                                    background: rgba(palette.plum, 0.06),
                                    '&:hover': { background: rgba(palette.plum, 0.14) },
                                }}
                            >
                                <PsychologyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="View Profile">
                            <IconButton
                                size="small"
                                onClick={onViewProfile}
                                sx={{
                                    width: 32, height: 32, borderRadius: '10px',
                                    color: palette.teal,
                                    border: `1px solid ${rgba(palette.teal, 0.2)}`,
                                    background: rgba(palette.teal, 0.06),
                                    '&:hover': { background: rgba(palette.teal, 0.14) },
                                }}
                            >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={onConnect}
                            startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 1.5, py: 0.5,
                                color: '#fff', fontSize: '0.7rem',
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 8px 20px -8px ${rgba(palette.rust, 0.6)}`,
                                '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                            }}
                        >
                            Connect
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   9 · SUGGESTED MEETING CARD
   ═══════════════════════════════════════════════════════════════════════════ */

const SuggestedMeetingCard = ({ suggestion, onViewProfile, onConnect, index }) => {
    const company = suggestion.company || {};

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: '18px',
                    border: `1px solid ${palette.hairlineSoft}`,
                    background: rgba('#FFFFFF', 0.6),
                    backdropFilter: 'blur(8px)',
                    height: '100%',
                    transition: 'all .3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px ${rgba(palette.ink, 0.06)}`,
                        borderColor: rgba(palette.teal, 0.2),
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <CompanyAvatar company={company} size={44} radius={12} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: palette.ink }}>
                                {company?.companyName || 'Unknown Company'}
                            </Typography>
                            {company?.isVerified && <VerifiedIcon sx={{ fontSize: 14, color: palette.pine }} />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <MetaPill label={suggestion.matchType || 'Match'} tone={palette.teal} />
                            <MetaPill label={`${suggestion.score || 0}% match`} tone={palette.amber} />
                            {suggestion.suggestedTime && (
                                <MetaPill
                                    label={suggestion.suggestedTime}
                                    tone={palette.rust}
                                    icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft, lineHeight: 1.5, mb: 1.5 }}>
                    <LightbulbIcon sx={{ fontSize: 14, color: palette.amber, verticalAlign: 'middle', mr: 0.5 }} />
                    {suggestion.reason || 'Potential partnership opportunity based on AI matching.'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.75, pt: 1.5, borderTop: `1px solid ${palette.hairlineSoft}` }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onViewProfile(company?.id)}
                        sx={{
                            borderRadius: '10px', textTransform: 'none', fontSize: '0.7rem',
                            borderColor: palette.teal, color: palette.teal,
                            '&:hover': { borderColor: palette.teal, bgcolor: rgba(palette.teal, 0.08) },
                        }}
                    >
                        Profile
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={() => onConnect(company?.id)}
                        startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: '10px', fontSize: '0.7rem', px: 1.5, py: 0.5,
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 8px 20px -8px ${rgba(palette.rust, 0.6)}`,
                            '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                        }}
                    >
                        Connect
                    </Button>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   10 · AI ANALYSIS DIALOG
   ═══════════════════════════════════════════════════════════════════════════ */

const AIAnalysisDialog = ({ open, company, onClose, onSendMessage }) => {
    const [aiMessage, setAiMessage] = useState('');
    const [aiScore, setAiScore] = useState(0);
    const [aiDetails, setAiDetails] = useState([]);
    const [mutualBenefit, setMutualBenefit] = useState(false);
    const [providerToSeeker, setProviderToSeeker] = useState([]);
    const [seekerToProvider, setSeekerToProvider] = useState([]);
    const [aiInsights, setAiInsights] = useState({ summary: '', recommendation: '', subject: '' });

    useEffect(() => {
        if (company && open) {
            const userCompany = JSON.parse(localStorage.getItem('userData') || '{}');
            const result = calculateAIMatchScore(userCompany, company);
            setAiScore(result.score);
            setAiDetails(result.details || []);
            setMutualBenefit(result.mutualBenefit || false);
            setProviderToSeeker(result.providerToSeeker || []);
            setSeekerToProvider(result.seekerToProvider || []);
            setAiInsights(result.aiInsights || { summary: '', recommendation: '', subject: '' });

            const aiMsg = generateAIMessage(
                userCompany,
                company,
                result.score,
                result.details,
                result.mutualBenefit
            );
            setAiMessage(aiMsg.message);
        }
    }, [company, open]);

    if (!company) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(palette.plum, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.plum, 0.1)})`,
                    maxHeight: '92vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <GlassIcon icon={<PsychologyIcon />} color={palette.plum} size={46} />
                    <Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                            AI Analysis
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                            {company.companyName || 'Company'} · AI-powered insights
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08) },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 2.75 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={Math.min(aiScore, 100)}
                                    size={80}
                                    thickness={6}
                                    sx={{
                                        color: aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.amber : palette.rust,
                                        '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                                    }}
                                />
                                <Box sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                }}>
                                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.5rem', color: palette.ink }}>
                                        {Math.round(aiScore)}%
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600, color: palette.inkLight, mt: 0.5 }}>
                                Match Score
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                {aiScore >= 80 ? '🌟 Exceptional Match!' :
                                 aiScore >= 60 ? '🤝 Strong Match' :
                                 aiScore >= 40 ? '📈 Potential Match' :
                                 '🔍 Exploratory Match'}
                            </Typography>
                            {mutualBenefit && (
                                <Chip
                                    icon={<HandshakeIcon sx={{ fontSize: 14 }} />}
                                    label="🎯 Mutual Benefit Detected"
                                    sx={{ mt: 0.5, bgcolor: `${palette.pine}12`, color: palette.pine, fontWeight: 600 }}
                                />
                            )}
                            {aiInsights.summary && (
                                <Typography sx={{ mt: 0.75, fontSize: '0.85rem', color: palette.inkSoft }}>
                                    {aiInsights.summary}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {providerToSeeker.length > 0 && (
                        <GlassPanel accent={palette.pine} delay={0} padding={{ xs: 2, md: 2 }}>
                            <PanelHead icon={<HandshakeIcon />} title="Provider → Seeker" accent={palette.pine} dense />
                            {providerToSeeker.map((match, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.5 }}>
                                    <CheckIcon sx={{ fontSize: 14, color: palette.pine, mt: 0.25 }} />
                                    <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft }}>
                                        <strong>{match.providerName}</strong> provides <strong>{match.providerService}</strong> → <strong>{match.seekerName}</strong> needs <strong>{match.seekerNeed}</strong>
                                    </Typography>
                                </Box>
                            ))}
                        </GlassPanel>
                    )}

                    {seekerToProvider.length > 0 && (
                        <GlassPanel accent={palette.plum} delay={0.06} padding={{ xs: 2, md: 2 }}>
                            <PanelHead icon={<StarsIcon />} title="Seeker → Provider" accent={palette.plum} dense />
                            {seekerToProvider.map((match, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.5 }}>
                                    <CheckIcon sx={{ fontSize: 14, color: palette.plum, mt: 0.25 }} />
                                    <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft }}>
                                        <strong>{match.providerName}</strong> provides <strong>{match.providerService}</strong> → <strong>{match.seekerName}</strong> needs <strong>{match.seekerNeed}</strong>
                                    </Typography>
                                </Box>
                            ))}
                        </GlassPanel>
                    )}

                    {aiDetails.length > 0 && (
                        <GlassPanel accent={palette.amber} delay={0.12} padding={{ xs: 2, md: 2 }}>
                            <PanelHead icon={<LightbulbIcon />} title="Match Details" accent={palette.amber} dense />
                            {aiDetails.slice(0, 6).map((detail, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}>
                                    <CheckIcon sx={{ fontSize: 13, color: palette.amber, mt: 0.25 }} />
                                    <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft }}>
                                        {detail}
                                    </Typography>
                                </Box>
                            ))}
                        </GlassPanel>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={aiMessage}
                        onChange={(e) => setAiMessage(e.target.value)}
                        label="AI Generated Message"
                        sx={glassField(palette.plum)}
                        slotProps={{ input: {
                            sx: {
                                fontSize: '0.85rem',
                                lineHeight: 1.7,
                            }
                        } }}
                    />
                </Box>
            </DialogContent>

            <Box
                sx={{
                    px: 2.75, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25,
                    borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45),
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px' }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={() => onSendMessage(aiMessage)}
                    startIcon={<SendIcon />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                        background: `linear-gradient(140deg, ${palette.plum}, ${rgba(palette.plum, 0.72)})`,
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.plum, 0.95)}`,
                        '&:hover': { background: `linear-gradient(140deg, ${palette.plumGlow}, ${palette.plum})` },
                    }}
                >
                    Send AI Message
                </Button>
            </Box>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   11 · MAIN MATCHMAKING COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const Matchmaking = () => {
    const navigate = useNavigate();
    const { user, userData, companyData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [companies, setCompanies] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [filteredRecommendations, setFilteredRecommendations] = useState([]);
    const [suggestedMeetings, setSuggestedMeetings] = useState([]);
    const [filters, setFilters] = useState({
        minScore: 0,
        region: '',
        service: '',
        type: '',
        mutualBenefitOnly: false,
    });
    const [openAIDialog, setOpenAIDialog] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    const [stats, setStats] = useState({
        totalMatches: 0,
        highQualityMatches: 0,
        pendingConnections: 0,
        suggestedMeetings: 0,
        mutualBenefitMatches: 0,
    });

    /* ── fetch data ───────────────────────────────────────────────────────── */

    const fetchCompanies = useCallback(async () => {
        try {
            const snapshot = await getDocs(collection(db, 'companies'));
            const list = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            return list;
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast.error('Failed to load companies');
            return [];
        }
    }, []);

    const fetchRecommendations = useCallback(async () => {
        try {
            const snapshot = await getDocs(collection(db, 'recommendations'));
            const list = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            return list;
        } catch (error) {
            console.error('Error fetching recommendations:', error);
            return [];
        }
    }, []);

    const loadData = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true);
        else setLoading(true);

        try {
            const [companiesList, recsList] = await Promise.all([
                fetchCompanies(),
                fetchRecommendations(),
            ]);

            setCompanies(companiesList);

            // Process recommendations with company data
            const processed = recsList.map(rec => ({
                ...rec,
                company: companiesList.find(c => c.id === rec.companyId) || rec.company || { companyName: rec.companyName || 'Unknown' },
            }));

            // Sort by score descending
            processed.sort((a, b) => (b.score || 0) - (a.score || 0));
            setRecommendations(processed);
            setFilteredRecommendations(processed);

            // Calculate stats
            const total = processed.length;
            const highQuality = processed.filter(r => (r.score || 0) >= 80).length;
            const mutualBenefit = processed.filter(r => r.mutualBenefit).length;

            setStats({
                totalMatches: total,
                highQualityMatches: highQuality,
                pendingConnections: processed.filter(r => r.status === 'pending').length,
                suggestedMeetings: Math.min(5, processed.filter(r => (r.score || 0) >= 60).length),
                mutualBenefitMatches: mutualBenefit,
            });

            // Generate suggested meetings
            const suggestions = processed
                .filter(r => (r.score || 0) >= 60)
                .slice(0, 6)
                .map((rec, index) => ({
                    id: rec.id,
                    company: rec.company,
                    score: rec.score || 0,
                    reason: rec.mutualBenefit
                        ? 'Mutual benefit opportunity - both companies have what the other needs'
                        : ['Similar service interests', 'Complementary business goals', 'Regional presence overlap',
                           'Previous interaction history', 'Mutual connections', 'Industry alignment'][index % 6],
                    suggestedTime: ['Today 2:00 PM', 'Tomorrow 10:00 AM', 'Tomorrow 3:30 PM',
                                   'Wed 11:00 AM', 'Thu 1:00 PM', 'Fri 9:30 AM'][index % 6],
                    matchType: rec.mutualBenefit ? 'Mutual Benefit' :
                              ['Service Match', 'Regional Match', 'Goal Alignment', 'Synergy',
                               'Complementary', 'Strategic'][index % 6],
                }));
            setSuggestedMeetings(suggestions);

        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load matchmaking data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [fetchCompanies, fetchRecommendations]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    /* ── AI analysis ──────────────────────────────────────────────────────── */

    const runAIAnalysis = useCallback(async () => {
        setIsAnalyzing(true);
        toast.loading('AI is analyzing business connections...', { id: 'ai-analysis' });

        try {
            const userCompany = companyData || userData || {};
            const companiesList = await fetchCompanies();

            // Filter out the user's own company
            const otherCompanies = companiesList.filter(c => c.id !== userCompany.id);

            const newRecommendations = [];

            for (const target of otherCompanies) {
                const result = calculateAIMatchScore(userCompany, target);

                newRecommendations.push({
                    companyId: target.id,
                    companyName: target.companyName || 'Unknown Company',
                    company: target,
                    score: result.score,
                    matchDetails: result.details || [],
                    mutualBenefit: result.mutualBenefit || false,
                    providerToSeeker: result.providerToSeeker || [],
                    seekerToProvider: result.seekerToProvider || [],
                    status: 'pending',
                    aiGenerated: true,
                    createdAt: serverTimestamp(),
                });
            }

            // Sort by score descending
            newRecommendations.sort((a, b) => b.score - a.score);

            // Save top recommendations to Firestore (limit to 20)
            const topRecs = newRecommendations.slice(0, 20);
            for (const rec of topRecs) {
                const { company, ...recData } = rec;
                await addDoc(collection(db, 'recommendations'), recData);
            }

            // Update local state
            const processed = topRecs.map(rec => ({
                ...rec,
                company: companiesList.find(c => c.id === rec.companyId) || rec.company,
            }));

            setRecommendations(processed);
            setFilteredRecommendations(processed);

            const mutualBenefitCount = topRecs.filter(r => r.mutualBenefit).length;
            setStats({
                totalMatches: topRecs.length,
                highQualityMatches: topRecs.filter(r => r.score >= 80).length,
                pendingConnections: topRecs.filter(r => r.status === 'pending').length,
                suggestedMeetings: Math.min(5, topRecs.filter(r => r.score >= 60).length),
                mutualBenefitMatches: mutualBenefitCount,
            });

            // Regenerate suggestions
            const suggestions = topRecs
                .filter(r => r.score >= 60)
                .slice(0, 6)
                .map((rec, index) => ({
                    id: rec.id,
                    company: rec.company,
                    score: rec.score,
                    reason: rec.mutualBenefit
                        ? 'Mutual benefit opportunity - both companies have what the other needs'
                        : ['Similar service interests', 'Complementary business goals', 'Regional presence overlap',
                           'Previous interaction history', 'Mutual connections', 'Industry alignment'][index % 6],
                    suggestedTime: ['Today 2:00 PM', 'Tomorrow 10:00 AM', 'Tomorrow 3:30 PM',
                                   'Wed 11:00 AM', 'Thu 1:00 PM', 'Fri 9:30 AM'][index % 6],
                    matchType: rec.mutualBenefit ? 'Mutual Benefit' :
                              ['Service Match', 'Regional Match', 'Goal Alignment', 'Synergy',
                               'Complementary', 'Strategic'][index % 6],
                }));
            setSuggestedMeetings(suggestions);

            toast.success(`AI analysis complete! Found ${topRecs.length} potential matches with ${mutualBenefitCount} mutual benefit opportunities.`, { id: 'ai-analysis' });

        } catch (error) {
            console.error('Error running AI analysis:', error);
            toast.error('Failed to run AI analysis. Please try again.', { id: 'ai-analysis' });
        } finally {
            setIsAnalyzing(false);
        }
    }, [companyData, userData, fetchCompanies]);

    /* ── handlers ─────────────────────────────────────────────────────────── */

    const handleConnect = async (companyId) => {
        try {
            const connectionData = {
                companyId: companyId,
                userId: user?.uid,
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            await addDoc(collection(db, 'connections'), connectionData);
            toast.success('Connection request sent successfully!');
            loadData(true);
        } catch (error) {
            console.error('Error sending connection request:', error);
            toast.error('Failed to send connection request');
        }
    };

    const handleViewProfile = (companyId) => {
        if (companyId) navigate(`/companies/${companyId}`);
    };

    const handleAIAnalysis = (company) => {
        setSelectedCompany(company);
        setOpenAIDialog(true);
    };

    const handleSendAIMessage = async (message) => {
        try {
            if (!selectedCompany) {
                toast.error('No company selected');
                return;
            }
            // Here you would send the message to the company
            toast.success(`AI message sent to ${selectedCompany.companyName || 'Company'}!`);
            setOpenAIDialog(false);
        } catch (error) {
            console.error('Error sending AI message:', error);
            toast.error('Failed to send AI message');
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        applyFilters({ ...filters, [field]: value });
    };

    const applyFilters = (currentFilters) => {
        let filtered = [...recommendations];

        if (currentFilters.minScore > 0) {
            filtered = filtered.filter(r => (r.score || 0) >= currentFilters.minScore);
        }
        if (currentFilters.mutualBenefitOnly) {
            filtered = filtered.filter(r => r.mutualBenefit);
        }
        if (currentFilters.region) {
            filtered = filtered.filter(r => {
                const regions = [r.company?.primaryRegion, r.company?.secondaryRegion, ...(r.company?.regions || [])]
                    .filter(Boolean).map(rg => safeString(rg));
                return regions.includes(safeString(currentFilters.region));
            });
        }
        if (currentFilters.service) {
            const serviceLower = safeString(currentFilters.service);
            filtered = filtered.filter(r => {
                const services = [r.company?.primaryService, r.company?.secondaryService, ...(r.company?.services || [])]
                    .filter(Boolean).map(s => safeString(s));
                return services.some(s => s.includes(serviceLower) || serviceLower.includes(s));
            });
        }
        if (currentFilters.type) {
            filtered = filtered.filter(r => r.company?.companyType === currentFilters.type);
        }

        setFilteredRecommendations(filtered);
    };

    const clearFilters = () => {
        const emptyFilters = { minScore: 0, region: '', service: '', type: '', mutualBenefitOnly: false };
        setFilters(emptyFilters);
        setFilteredRecommendations(recommendations);
    };

    const filtersActive = Boolean(
        filters.minScore > 0 || filters.region || filters.service || filters.type || filters.mutualBenefitOnly
    );

    /* ── render ───────────────────────────────────────────────────────────── */

    const shell = {
        minHeight: '100vh', position: 'relative', fontFamily: fontBody, color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    const statCards = [
        {
            label: 'Total Matches',
            value: stats.totalMatches,
            icon: <PeopleIcon  key="config-1869-0-2" />,
            accent: palette.rust,
            hint: 'AI-discovered connections',
        },
        {
            label: 'High Quality',
            value: stats.highQualityMatches,
            icon: <StarIcon  key="config-1876-1-2" />,
            accent: palette.pine,
            hint: '80%+ match score',
        },
        {
            label: 'Pending',
            value: stats.pendingConnections,
            icon: <ScheduleIcon  key="config-1883-2-2" />,
            accent: palette.amber,
            hint: 'Awaiting response',
        },
        {
            label: 'Suggested',
            value: stats.suggestedMeetings,
            icon: <EventIcon  key="config-1890-3-2" />,
            accent: palette.teal,
            hint: 'Ready to schedule',
        },
        {
            label: 'Mutual Benefit',
            value: stats.mutualBenefitMatches,
            icon: <HandshakeIcon  key="config-1897-4-2" />,
            accent: palette.plum,
            hint: 'Win-win opportunities',
        },
    ];

    const tabs = [
        { label: 'Recommendations', icon: <RecommendIcon  key="config-1907-0-1" />, color: palette.rust },
        { label: 'Suggested Meetings', icon: <ScheduleIcon  key="config-1908-1-1" />, color: palette.pine },
        { label: 'Analytics', icon: <InsightsIcon  key="config-1909-2-1" />, color: palette.plum },
    ];

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── hero ──────────────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust, mb: 0.75,
                                }}
                            >
                                TelcoFair · Matchmaking
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em', lineHeight: 1.05, color: palette.ink,
                                }}
                            >
                                AI{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    matchmaking
                                </Box>
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}>
                                AI-powered business matching — find the perfect partners for your telecom services.
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.pine, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.pine, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <LiveIcon sx={{ fontSize: 9, color: palette.pine, animation: 'tfPulse 2s ease-in-out infinite' }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.pine }}>
                                        {compact(stats.highQualityMatches)} HIGH QUALITY
                                    </Typography>
                                </Box>
                                {stats.mutualBenefitMatches > 0 && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                            borderRadius: '999px', border: `1px solid ${rgba(palette.plum, 0.22)}`,
                                            background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.plum, 0.1)})`,
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <HandshakeIcon sx={{ fontSize: 13, color: palette.plum }} />
                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.plum }}>
                                            {compact(stats.mutualBenefitMatches)} MUTUAL BENEFIT
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, height: 30,
                                        borderRadius: '999px', border: `1px solid ${rgba(palette.teal, 0.22)}`,
                                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(palette.teal, 0.1)})`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <EventIcon sx={{ fontSize: 13, color: palette.teal }} />
                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.66rem', fontWeight: 600, color: palette.teal }}>
                                        {compact(stats.suggestedMeetings)} MEETINGS READY
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Button
                                onClick={() => loadData(true)}
                                disabled={refreshing}
                                startIcon={<RefreshIcon sx={{ animation: refreshing ? 'tfSpin 1s linear infinite' : 'none' }} />}
                                sx={{
                                    px: 2.25, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 600,
                                    fontSize: '0.82rem', color: palette.inkSoft,
                                    border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.8)}, ${rgba('#FFFFFF', 0.45)})`,
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}, 0 8px 20px -12px ${rgba(palette.ink, 0.5)}`,
                                    '&:hover': { color: palette.ink, background: rgba('#FFFFFF', 0.9) },
                                }}
                            >
                                {refreshing ? 'Refreshing' : 'Refresh'}
                            </Button>
                            <Button
                                onClick={runAIAnalysis}
                                disabled={isAnalyzing}
                                startIcon={isAnalyzing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
                                sx={{
                                    px: 2.5, py: 1, borderRadius: '14px', textTransform: 'none', fontWeight: 700,
                                    fontSize: '0.82rem', color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.plum}, ${palette.plumGlow})`,
                                    border: `1px solid ${rgba('#FFFFFF', 0.25)}`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.35)}, 0 14px 30px -14px ${rgba(palette.plum, 1)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.plumGlow}, ${palette.plum})` },
                                    '&:disabled': { opacity: 0.6 },
                                }}
                            >
                                {isAnalyzing ? 'Analyzing...' : 'Run AI Analysis'}
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── tabs ──────────────────────────────────────────────────── */}
                <GlassPanel accent={palette.rust} delay={0.1} padding={{ xs: 1.75, md: 2.5 }}>
                    <Box sx={{ borderBottom: `1px solid ${palette.hairlineSoft}`, px: 1 }}>
                        <Tabs
                            value={tabValue}
                            onChange={(_, newValue) => setTabValue(newValue)}
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                                    fontFamily: fontBody, color: palette.inkLight,
                                    minHeight: 44, px: 1.5,
                                    '&.Mui-selected': { color: palette.ink },
                                    '&:hover': { color: palette.rust },
                                },
                                '& .MuiTabs-indicator': { height: 3, borderRadius: 3, backgroundColor: palette.rust },
                            }}
                        >
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={i}
                                    icon={React.cloneElement(tab.icon, { sx: { fontSize: 17 } })}
                                    iconPosition="start"
                                    label={tab.label}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ px: { xs: 0, sm: 1 } }}>
                        {/* ── RECOMMENDATIONS TAB ── */}
                        <TabPanel value={tabValue} index={0}>
                            <PanelHead
                                icon={<RecommendIcon />}
                                title="AI Recommendations"
                                subtitle={`${compact(filteredRecommendations.length)} matches`}
                                accent={palette.rust}
                            />

                            {/* Filters */}
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
                                <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                                    <InputLabel>Min Score</InputLabel>
                                    <Select
                                        value={filters.minScore}
                                        label="Min Score"
                                        onChange={(e) => handleFilterChange('minScore', Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>All Scores</MenuItem>
                                        <MenuItem value={40}>40%+</MenuItem>
                                        <MenuItem value={60}>60%+</MenuItem>
                                        <MenuItem value={75}>75%+</MenuItem>
                                        <MenuItem value={85}>85%+</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                                    <InputLabel>Region</InputLabel>
                                    <Select
                                        value={filters.region}
                                        label="Region"
                                        onChange={(e) => handleFilterChange('region', e.target.value)}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        {REGIONS.map((r) => (
                                            <MenuItem key={r} value={r}>{r}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                                    <InputLabel>Service</InputLabel>
                                    <Select
                                        value={filters.service}
                                        label="Service"
                                        onChange={(e) => handleFilterChange('service', e.target.value)}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        {SERVICE_CATEGORIES.slice(0, 10).map((s) => (
                                            <MenuItem key={s} value={s}>{s}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140, ...glassField(palette.rust) }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={filters.type}
                                        label="Type"
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        {COMPANY_TYPES.map((t) => (
                                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={filters.mutualBenefitOnly}
                                            onChange={(e) => handleFilterChange('mutualBenefitOnly', e.target.checked)}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                            }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <HandshakeIcon sx={{ fontSize: 16, color: palette.pine }} />
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: palette.ink }}>
                                                Mutual Benefit
                                            </Typography>
                                        </Box>
                                    }
                                />

                                <AnimatePresence initial={false}>
                                    {filtersActive && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Button
                                                onClick={clearFilters}
                                                startIcon={<FilterOffIcon sx={{ fontSize: 17 }} />}
                                                sx={{
                                                    textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 1.75,
                                                    color: palette.rust, border: `1px solid ${rgba(palette.rust, 0.3)}`,
                                                    background: rgba(palette.rust, 0.06),
                                                    '&:hover': { background: rgba(palette.rust, 0.12) },
                                                }}
                                            >
                                                Clear
                                            </Button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </Box>

                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <Box key={i} sx={{ mb: 2 }}>
                                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px', bgcolor: palette.hairline }} />
                                    </Box>
                                ))
                            ) : filteredRecommendations.length === 0 ? (
                                <EmptyState
                                    icon={<RecommendIcon />}
                                    title={recommendations.length === 0 ? 'No recommendations yet' : 'No matches found'}
                                    text={recommendations.length === 0
                                        ? 'Run the AI analysis to discover potential business partners.'
                                        : 'Try adjusting your filters to see more matches.'}
                                    action={recommendations.length === 0 ? 'Run AI Analysis' : undefined}
                                    onAction={recommendations.length === 0 ? runAIAnalysis : undefined}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {filteredRecommendations.map((rec, index) => (
                                        <RecommendationCard
                                            key={rec.id}
                                            company={rec.company || { companyName: rec.companyName || 'Unknown' }}
                                            score={rec.score || 0}
                                            matchDetails={rec.matchDetails || []}
                                            mutualBenefit={rec.mutualBenefit || false}
                                            providerToSeeker={rec.providerToSeeker || []}
                                            seekerToProvider={rec.seekerToProvider || []}
                                            onConnect={() => handleConnect(rec.companyId)}
                                            onViewProfile={() => handleViewProfile(rec.companyId)}
                                            onAIAnalysis={handleAIAnalysis}
                                            index={index}
                                        />
                                    ))}
                                </Box>
                            )}
                        </TabPanel>

                        {/* ── SUGGESTED MEETINGS TAB ── */}
                        <TabPanel value={tabValue} index={1}>
                            <PanelHead
                                icon={<ScheduleIcon />}
                                title="Suggested Meetings"
                                subtitle={`${compact(suggestedMeetings.length)} ready to schedule`}
                                accent={palette.pine}
                            />

                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <Box key={i} sx={{ mb: 2 }}>
                                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px', bgcolor: palette.hairline }} />
                                    </Box>
                                ))
                            ) : suggestedMeetings.length === 0 ? (
                                <EmptyState
                                    icon={<EventIcon />}
                                    title="No suggested meetings"
                                    text="Run the AI analysis to get personalized meeting suggestions."
                                    action="Run AI Analysis"
                                    onAction={runAIAnalysis}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    }}
                                >
                                    {suggestedMeetings.map((suggestion, index) => (
                                        <SuggestedMeetingCard
                                            key={suggestion.id || index}
                                            suggestion={suggestion}
                                            onViewProfile={handleViewProfile}
                                            onConnect={handleConnect}
                                            index={index}
                                        />
                                    ))}
                                </Box>
                            )}
                        </TabPanel>

                        {/* ── ANALYTICS TAB ── */}
                        <TabPanel value={tabValue} index={2}>
                            <PanelHead
                                icon={<InsightsIcon />}
                                title="Matchmaking Analytics"
                                subtitle="AI insights & performance metrics"
                                accent={palette.plum}
                            />

                            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                <GlassPanel accent={palette.plum} delay={0} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<DonutLargeIcon />} title="Match Quality Distribution" accent={palette.plum} dense />
                                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                                        {[
                                            { label: 'Excellent (80%+)', value: stats.highQualityMatches, color: palette.pine },
                                            { label: 'Good (60-79%)', value: Math.max(0, stats.totalMatches - stats.highQualityMatches - Math.floor(stats.totalMatches * 0.25)), color: palette.amber },
                                            { label: 'Fair (40-59%)', value: Math.floor(stats.totalMatches * 0.25), color: palette.rust },
                                            { label: 'Low (<40%)', value: Math.max(0, stats.totalMatches - stats.highQualityMatches - Math.floor(stats.totalMatches * 0.25) - Math.floor(stats.totalMatches * 0.25)), color: palette.inkLight },
                                        ].map((item) => (
                                            <Box key={item.label}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>{item.label}</Typography>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.75rem', fontWeight: 600, color: palette.ink }}>
                                                        {item.value}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ height: 4, borderRadius: 3, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${stats.totalMatches > 0 ? (item.value / stats.totalMatches) * 100 : 0}%` }}
                                                        transition={{ duration: 0.8, ease: 'easeOut' }}
                                                        style={{
                                                            height: '100%',
                                                            borderRadius: 3,
                                                            background: item.color,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </GlassPanel>

                                <GlassPanel accent={palette.amber} delay={0.06} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<LightbulbIcon />} title="Key Match Factors" accent={palette.amber} dense />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {[
                                            { label: 'Service Exchange', value: 92 },
                                            { label: 'Regional Presence', value: 78 },
                                            { label: 'Business Goals', value: 85 },
                                            { label: 'Company Synergy', value: 70 },
                                            { label: 'Verification', value: 65 },
                                        ].map((item) => (
                                            <Box key={item.label}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>{item.label}</Typography>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.75rem', fontWeight: 600, color: palette.ink }}>
                                                        {item.value}%
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ height: 4, borderRadius: 3, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${item.value}%` }}
                                                        transition={{ duration: 0.8, ease: 'easeOut' }}
                                                        style={{
                                                            height: '100%',
                                                            borderRadius: 3,
                                                            background: `linear-gradient(90deg, ${palette.rust}, ${palette.amber})`,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </GlassPanel>

                                <GlassPanel accent={palette.pine} delay={0.12} padding={{ xs: 2, md: 2.25 }} sx={{ gridColumn: { md: '1 / -1' } }}>
                                    <PanelHead icon={<PsychologyIcon />} title="AI Insights" accent={palette.pine} dense />
                                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkFaint }}>
                                                Top Match
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: palette.ink, mt: 0.25 }}>
                                                {recommendations.length > 0 && recommendations[0]?.company?.companyName
                                                    ? `${recommendations[0].company.companyName} (${recommendations[0].score}%)`
                                                    : 'No matches yet'}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, mt: 0.5 }}>
                                                {recommendations.length > 0 && recommendations[0]?.matchDetails?.length > 0
                                                    ? recommendations[0].matchDetails[0]
                                                    : 'Run AI analysis to discover matches'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.amber, 0.06), border: `1px solid ${rgba(palette.amber, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkFaint }}>
                                                Mutual Benefits
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: palette.ink, mt: 0.25 }}>
                                                {stats.mutualBenefitMatches} opportunities
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, mt: 0.5 }}>
                                                Both companies have what the other needs
                                            </Typography>
                                        </Box>
                                    </Box>
                                </GlassPanel>
                            </Box>
                        </TabPanel>
                    </Box>
                </GlassPanel>
            </Container>

            {/* ── AI ANALYSIS DIALOG ── */}
            <AIAnalysisDialog
                open={openAIDialog}
                company={selectedCompany}
                onClose={() => setOpenAIDialog(false)}
                onSendMessage={handleSendAIMessage}
            />

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Matchmaking;